/**
 * Selection setup: the laser/substrate pair and the tag.
 *
 * The selection ID is composed rather than typed - substrate, laser, today's
 * date and the tag - so that a palette always names the combination it is valid
 * for. A palette is only meaningful on the setup it was measured on.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField, Stack, Button, MenuItem, Typography, Box, Chip,
} from "@mui/material";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import {
  selectionSidebar,
  SELECTION_TAG_PATTERN,
  composeSelectionName,
  nextSelectionTag,
  substrateToken,
  dateStamp,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";
import { useSelectionDraft } from "./SelectionSetupFlow";

const TAG_FORMAT_HINT = "Tag: free text (the next free SEL number is pre-filled as a suggestion)";

export default function ColorSelectionSetUpName() {
  // The user types only the tag; the identifier is composed from the chosen
  // substrate + laser + today's date + tag (see helper/selectionFlow.js).
  const [selectionTag, setSelectionTag] = useState("");
  const [suggestedTag, setSuggestedTag] = useState("");
  const [selectionDescription, setSelectionDescription] = useState("");
  const [tagError, setTagError] = useState("");
  const [open, setOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialSelectionObject, setMaterialSelectionObject] = useState("");
  const [materialSelection, setMaterialSelection] = useState("");
  const [materialSelectionName, setMaterialSelectionName] = useState("");
  const [laserDevices, setLaserDevices] = useState([]);
  const [laserSystemSelectionObject, setLaserSystemSelectionObject] = useState("");
  const [laserSystemSelection, setLaserSystemSelection] = useState("");
  const [laserSystemSelectionName, setLaserSystemSelectionName] = useState("");

  const navigate = useNavigate();
  // The kind is the URL's (/colorselections/<kind>/new/name); the choices
  // go to the wizard's draft, the selection is created on the next step.
  const { selectionType, kind, listRoute, setupRoute } = useSelectionParams();
  const { draft, updateDraft } = useSelectionDraft();

  const validateSelectionTag = (tag) => SELECTION_TAG_PATTERN.test(tag);

  const basisChosen = Boolean(materialSelectionName && laserSystemSelectionName);
  const composedName = useMemo(
    () => (basisChosen && selectionTag
      ? composeSelectionName(materialSelectionName, laserSystemSelectionName, selectionTag)
      : ""),
    [basisChosen, materialSelectionName, laserSystemSelectionName, selectionTag]
  );

  const handleLaserSystemSelection = (event) => {
    const selected = event.target.value;
    setLaserSystemSelectionObject(selected);
    setLaserSystemSelection(selected.id);
    setLaserSystemSelectionName(selected.name);
  };

  const handleMaterialSelection = (event) => {
    const selected = event.target.value;
    setMaterialSelectionObject(selected);
    setMaterialSelection(selected.id);
    setMaterialSelectionName(selected.name);
  };

  const handleSelectionTagChange = (event) => {
    const value = event.target.value;
    setSelectionTag(value);
    if (value && !validateSelectionTag(value)) {
      setTagError("The tag must not start or end with a space");
    } else {
      setTagError("");
    }
  };

  const nextPageClickFunc = async (navigatePageName) => {
    if (!validateSelectionTag(selectionTag) || !composedName) {
      setTagError("Please enter a selection tag");
      setFormatOpen(true);
      return;
    }
    var proceed = false;
    await axios
      .get("api/color/selection/?name=" + encodeURIComponent(composedName))
      .then((res) => {
        if (res.status === 200) {
          proceed = res.data.length === 0;
        }
      })
      .catch((err) => {
        console.error(err);
      });
    if (!proceed) {
      setOpen(true);
    } else {
      updateDraft({
        laserId: laserSystemSelection,
        laserName: laserSystemSelectionName,
        materialId: materialSelection,
        materialName: materialSelectionName,
        description: selectionDescription,
        tag: selectionTag,
        name: composedName,
      });
      navigate(navigatePageName);
    }
  };

  // Suggest the next free SEL### for the chosen substrate/laser pair. Only
  // overwrites the field while it still holds our previous suggestion (or is
  // empty), never a tag the user typed.
  useEffect(() => {
    if (!basisChosen || !laserSystemSelection) return undefined;
    let cancelled = false;
    axios
      .get(`api/selection/data?laser=${laserSystemSelection}&page=0&per_page=1000`)
      .then((res) => {
        if (cancelled || res.status !== 200) return;
        const names = ((res.data && res.data.data) || []).map((row) => row.name);
        const suggestion = nextSelectionTag(names, materialSelectionName, laserSystemSelectionName);
        setSuggestedTag(suggestion);
        setSelectionTag((current) => {
          if (current === "" || current === suggestedTag) {
            setTagError("");
            return suggestion;
          }
          return current;
        });
      })
      .catch((err) => console.error("Error fetching existing selections: " + err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basisChosen, laserSystemSelection, materialSelectionName, laserSystemSelectionName]);

  useEffect(() => {
    // Back from Choose Explorations: the draft's choices are shown again.
    if (draft.tag) setSelectionTag(draft.tag);
    if (draft.description) setSelectionDescription(draft.description);
    axios
      .get("api/color/explorer/laser/device")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const devices = res.data.map((item) => ({ id: item.id, name: item.laser_device_name }));
          setLaserDevices(devices);
          const stored = devices.find((item) => item.id === draft.laserId);
          if (stored) {
            setLaserSystemSelectionObject(stored);
            setLaserSystemSelection(stored.id);
            setLaserSystemSelectionName(stored.name);
          }
        }
      })
      .catch((err) => console.error("Error fetching lasers: " + err));
    axios
      .get("api/color/explorer/material")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const rows = res.data.map((item) => ({ id: item.id, name: item.material_name }));
          setMaterials(rows);
          const stored = rows.find((item) => item.id === draft.materialId);
          if (stored) {
            setMaterialSelectionObject(stored);
            setMaterialSelection(stored.id);
            setMaterialSelectionName(stored.name);
          }
        }
      })
      .catch((err) => console.error("Error fetching materials: " + err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewText = composedName
    ? `Selection ID: ${composedName}`
    : "Selection ID: pick the laser system and substrate, then enter the tag";

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Setup"
        subtitle="Pick the laser/substrate combination the selection is based on and give it a tag; the selection ID is composed from these and today's date."
        stepSidebar={selectionSidebar("setup", null, selectionType)}
        headerBadges={<Chip size="small" label={kind.typeLabel} sx={{ fontWeight: 600, bgcolor: "#fff" }} />}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={listRoute}
              nextPage={setupRoute("explorations")}
              enableNextPage={
                selectionTag && selectionDescription && materialSelection && laserSystemSelection &&
                !tagError && validateSelectionTag(selectionTag) ? false : true
              }
              nextPageClickFunc={nextPageClickFunc}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ maxWidth: 640, mx: "auto", width: "100%" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              fullWidth
              label="Laser system"
              value={laserSystemSelectionObject}
              onChange={handleLaserSystemSelection}
            >
              {laserDevices.map((laserSystem) => (
                <MenuItem key={laserSystem.id} value={laserSystem}>{laserSystem.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Substrate"
              value={materialSelectionObject}
              onChange={handleMaterialSelection}
            >
              {materials.map((materialRow) => (
                <MenuItem key={materialRow.id} value={materialRow}>{materialRow.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            required
            id="selectionTag"
            label="Selection tag"
            placeholder="e.g., SEL001"
            onChange={handleSelectionTagChange}
            value={selectionTag}
            type="text"
            fullWidth
            error={!!tagError}
            helperText={tagError || TAG_FORMAT_HINT}
            inputProps={{ "data-testid": "selection-tag-input" }}
          />
          <Typography
            variant="body2"
            data-testid="selection-id-preview"
            sx={{ fontFamily: "monospace", alignSelf: "flex-start" }}
            color={composedName ? "text.primary" : "text.secondary"}
          >
            {previewText}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline', alignSelf: "flex-start" }}
            onClick={() => setFormatOpen(true)}
          >
            Click to see naming convention examples
          </Typography>
          <TextField
            required
            id="selectiondescription"
            label="Selection description"
            placeholder="Insert selection description"
            onChange={(event) => setSelectionDescription(event.target.value)}
            value={selectionDescription}
            type="text"
            multiline
            rows={4}
            fullWidth
          />
        </Stack>
      </ExplorationPageShell>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Error!</DialogTitle>
        <DialogContent sx={{ fontFamily: 'arial', marginTop: '2%', marginBottom: '2%' }}>
          A selection named {composedName} already exists. Please choose another tag.
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formatOpen} onClose={() => setFormatOpen(false)}>
        <DialogTitle>Selection naming convention</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Format:</Typography>
          <Typography variant="body1" gutterBottom>
            &lt;Substrate&gt;-&lt;Laser&gt;-&lt;Date&gt;-&lt;Tag&gt;
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            A selection can source samples from several explorations, so its ID does not
            contain an exploration name. Only the tag is typed; the rest is filled in from
            the chosen basis and the day the selection is named.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Components:</Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Substrate:</strong> material and alloy of the chosen substrate
            {materialSelectionName ? ` (${substrateToken(materialSelectionName)})` : " (e.g. SS-AISI304)"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Laser:</strong> the chosen laser system
            {laserSystemSelectionName ? ` (${laserSystemSelectionName})` : " (e.g. IPG2020)"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Date:</strong> today, YYYYMMDD ({dateStamp()})
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tag:</strong> free text of your choice (e.g. SEL001, customer-logo-2);
            the next free SEL number for the substrate/laser pair is pre-filled as a suggestion
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Examples:</Typography>
          <Typography variant="body1" gutterBottom>SS-AISI304-IPG2020-20260620-SEL001</Typography>
          <Typography variant="body1" gutterBottom>AL-1-Carbide-CB3-40W-20260827-SEL002</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormatOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
